// components/LineChartBase.jsx
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer, 
    Label,
    Area,
    AreaChart
} from 'recharts';
import {DOMAINS, useDomain} from "../../contexts/DomainContext";
import {useTheme} from "@mui/material";
import React from "react";

const LineChartBase = ({ data, lines,  disableDoubleClick = false, xAxisLabel, yAxisLabel, }) => {
    const theme = useTheme();
    const {domain} = useDomain();
    const isDarkMode = theme.palette.mode === 'dark';

// 도메인별 색상 설정
    const getTextColor = () => {
        if (domain === DOMAINS.PEMS) {
            return isDarkMode ? '#f0e6d9' : 'rgba(0, 0, 0, 0.87)';
        }
        return isDarkMode ? '#b3c5e6' : 'rgba(0, 0, 0, 0.87)';
    };

    const getTooltipBgColor = () => {
        if (domain === DOMAINS.PEMS) {
            return isDarkMode ? '#3d2814' : '#ffffff';
        }
        return isDarkMode ? '#1e3a5f' : '#ffffff';
    };

    const getTooltipBorderColor = () => {
        if (domain === DOMAINS.PEMS) {
            return isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
        }
        return isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
    };

    // 프로페셔널 & 차분한 블루/그린 계열 색상 팔레트
    const professionalColors = [
        { 
            line: '#3A5BA0', // 진한 파란색 - Professional Deep Blue
            gradient: { start: '#3A5BA0', end: '#2E4A85' },
            glow: 'rgba(58, 91, 160, 0.3)'
        },
        { 
            line: '#408E91', // 청록색 - Professional Teal
            gradient: { start: '#408E91', end: '#337478' },
            glow: 'rgba(64, 142, 145, 0.3)'
        },
        { 
            line: '#6AC4D9', // 밝은 하늘색 - Professional Sky Blue
            gradient: { start: '#6AC4D9', end: '#4FA8BD' },
            glow: 'rgba(106, 196, 217, 0.3)'
        },
        { 
            line: '#5B8EC0', // 중간 파란색 - Professional Medium Blue
            gradient: { start: '#5B8EC0', end: '#4976A3' },
            glow: 'rgba(91, 142, 192, 0.3)'
        },
        { 
            line: '#4A9DA6', // 중간 청록색 - Professional Medium Teal
            gradient: { start: '#4A9DA6', end: '#3C7E85' },
            glow: 'rgba(74, 157, 166, 0.3)'
        },
        { 
            line: '#7CD3E8', // 라이트 아쿠아 - Professional Light Aqua
            gradient: { start: '#7CD3E8', end: '#5BB8CF' },
            glow: 'rgba(124, 211, 232, 0.3)'
        }
    ];

    return (
        <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{top: 20, right: 10, left: 40, bottom: 5}}>
                <defs>
                    {lines.map((line, index) => {
                        const colorConfig = professionalColors[index % professionalColors.length];
                        return (
                            <linearGradient key={`gradient-${index}`} id={`gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={colorConfig.gradient.start} stopOpacity={0.4}/>
                                <stop offset="60%" stopColor={colorConfig.gradient.start} stopOpacity={0.2}/>
                                <stop offset="100%" stopColor={colorConfig.gradient.end} stopOpacity={0.05}/>
                            </linearGradient>
                        );
                    })}
                </defs>
                
                {/* 세련된 그리드 */}
                <CartesianGrid 
                    strokeDasharray="1 3" 
                    stroke={isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}
                    strokeWidth={1}
                />
                
                {/* X축 - 미니멀한 스타일 */}
                <XAxis
                    dataKey="timeLabel"
                    tick={false}
                    axisLine={false}
                    tickLine={false}
                >
                    {xAxisLabel && (
                        <Label
                            value={xAxisLabel}
                            position="insideBottom"
                            offset={5}
                            style={{ 
                                fill: getTextColor(), 
                                fontSize: 11, 
                                fontWeight: 500,
                                opacity: 0.7
                            }}
                        />
                    )}
                </XAxis>
                
                {/* Y축 - 모던한 스타일 */}
                <YAxis
                    domain={[0, (dataMax) => {
                        const padding = dataMax * 0.15; // 15% 여유 공간 추가
                        if (dataMax <= 3) return 3;
                        if (dataMax <= 5) return 5;
                        if (dataMax <= 10) return 10;
                        if (dataMax <= 50) return Math.ceil((dataMax + padding) / 10) * 10;
                        return Math.ceil((dataMax + padding) / 10) * 10;
                    }]}
                    axisLine={false}
                    tickLine={false}
                    tick={{
                        fill: getTextColor(),
                        fontSize: 11,
                        fontWeight: 500,
                        opacity: 0.8
                    }}
                    tickFormatter={(value) => `${value}W`}
                >
                    {yAxisLabel && (
                        <Label
                            value={yAxisLabel}
                            angle={-90}
                            position="insideLeft"
                            offset={5}
                            style={{ 
                                fill: getTextColor(), 
                                fontSize: 11, 
                                fontWeight: 600,
                                opacity: 0.9
                            }}
                        />
                    )}
                </YAxis>
                
                {/* 고급 툴팁 */}
                <Tooltip
                    contentStyle={{
                        backgroundColor: getTooltipBgColor(),
                        borderColor: getTooltipBorderColor(),
                        color: getTextColor(),
                        borderRadius: 12,
                        boxShadow: isDarkMode 
                            ? '0 8px 32px rgba(0, 0, 0, 0.6)' 
                            : '0 8px 32px rgba(0, 0, 0, 0.15)',
                        border: 'none',
                        fontSize: 12,
                        fontWeight: 500
                    }}
                    labelStyle={{ 
                        color: getTextColor(),
                        fontWeight: 600,
                        marginBottom: '8px'
                    }}
                    formatter={(value, name) => [
                        `${parseFloat(value).toFixed(2)} W`, 
                        name
                    ]}
                    cursor={{ 
                        stroke: getTextColor(), 
                        strokeWidth: 1, 
                        strokeDasharray: '4 4',
                        opacity: 0.5
                    }}
                />
                
                {/* 세련된 영역 차트 - 범례 제거 */}
                {lines.map((line, index) => {
                    const colorConfig = professionalColors[index % professionalColors.length];
                    return (
                        <Area
                            key={`area-${index}`}
                            type="monotone"
                            dataKey={line.key}
                            stroke={colorConfig.line}
                            fill={`url(#gradient-${index})`}
                            strokeWidth={2}
                            name={line.key}
                            dot={false}
                            activeDot={{
                                r: 5,
                                fill: colorConfig.line,
                                stroke: '#fff',
                                strokeWidth: 2,
                                filter: `drop-shadow(0 0 8px ${colorConfig.glow})`
                            }}
                            style={{
                                filter: `drop-shadow(0 2px 4px ${colorConfig.glow})`
                            }}
                        />
                    );
                })}
            </AreaChart>
        </ResponsiveContainer>
    );
};

export default LineChartBase;